const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const inrPaise = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const inrCompact = new Intl.NumberFormat('en-IN', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** ₹1,23,456 (Indian grouping) */
export function formatINR(amount: number | null | undefined): string {
  return `₹${inr.format(Math.round(amount ?? 0))}`;
}

/** ₹1,234.50 — paise precision, for per-ml costs and cost breakdowns */
export function formatINRPrecise(amount: number | null | undefined): string {
  return `₹${inrPaise.format(amount ?? 0)}`;
}

/** ₹1.2L / ₹45K — for chart axes */
export function formatINRCompact(amount: number): string {
  return `₹${inrCompact.format(amount)}`;
}

// ── ml ────────────────────────────────────────────────────────────────────────
// Every ml figure in this app is shown to EXACTLY one decimal place — the
// precision the bench measures to, the precision the services round to, and the
// precision inventory is stored in. "45.099999999999845 ml" and "45 ml" are
// both the wrong answer to "how much Sandalwood is on the shelf".
//
// The rule is "round half away from zero": 0.15 → 0.2, −0.15 → −0.2. Signed
// values appear here too (inventory transactions, adjustment deltas), so the
// asymmetry of a naive Math.round would show up on screen.
//
// Import these — do not write `.toFixed(1)` at the call site. The same rule
// lives, one copy per package, in:
//   services/order-service/src/services/rounding.ts
//   services/ecommerce-service/src/lib/rounding.ts
//   services/ai-service-v2/utils/rounding.py

/** Round to 1 dp, half away from zero. Non-finite input → 0. */
export function round1(x: number | null | undefined): number {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  return (n < 0 ? -Math.round(-n * 10) : Math.round(n * 10)) / 10;
}

/** 45.1 — one decimal, always, including the trailing zero (2.0, not 2). */
export function formatMlValue(ml: number | null | undefined): string {
  return round1(ml).toFixed(1);
}

/** 45.1 ml */
export function formatMl(ml: number | null | undefined): string {
  return `${formatMlValue(ml)} ml`;
}

/** +0.2 ml / −0.4 ml — for signed movements (transactions, adjustment deltas) */
export function formatMlSigned(ml: number | null | undefined): string {
  const n = round1(ml);
  return `${n < 0 ? '−' : '+'}${Math.abs(n).toFixed(1)} ml`;
}

/** 09 Jul 2026 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** 09 Jul 2026, 4:32 pm */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return `${formatDate(value)}, ${d
    .toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase()}`;
}

/** 09 Jul — for chart axes */
export function formatDateShort(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

/** "just now" / "5 mins ago" / "2 hours ago" */
export function timeAgo(date: Date | null): string {
  if (!date) return '—';
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return formatDateTime(date.toISOString());
}

/** First 8 chars of a UUID-ish id, uppercased: #A1B2C3D4 */
export function shortId(id: string): string {
  return `#${id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

export function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
